import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../services/api';
import { Check, Crown, Shield, Zap, Loader2, Users, Gift } from 'lucide-react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import TeamsWaitlistDialog from '../components/TeamsWaitlistDialog';
import PricingTerminalDemo from '../components/PricingTerminalDemo';
import Footer from '../components/Footer';

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    cadence: '/ forever',
    tagline: 'Get a feel for the platform.',
    cta: 'Current Plan',
    badge: null,
    color: '#00BFFF',
    features: [
      '5 rooms per month',
      'Limited AI hints',
      'Community leaderboard',
      'Basic skill tracking',
    ],
    missing: ['Interactive terminal', 'AI threat analysis', 'Certificates'],
  },
  {
    id: 'pro_monthly',
    name: 'Pro',
    price: '$15',
    cadence: '/ month',
    tagline: 'Everything you need to actually train.',
    cta: 'Upgrade to Pro',
    badge: 'Most Popular',
    color: '#00FF00',
    features: [
      'Unlimited rooms',
      'Real interactive terminal',
      'Full AI threat analysis',
      'Skill radar + certificates',
      'Priority support',
    ],
    missing: [],
  },
  {
    id: 'teams',
    name: 'Teams',
    price: '$49',
    cadence: '/ seat / month',
    tagline: 'Deploy cyber training across your org.',
    cta: 'Join Waitlist',
    badge: 'Coming Soon',
    color: '#9D00FF',
    features: [
      'Everything in Pro',
      'Org dashboard & analytics',
      'SSO (SAML / OIDC)',
      'Compliance reports (SOC2, ISO)',
      'Dedicated success manager',
    ],
    missing: [],
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, tier, trialUsed, refreshProfile } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loyalty, setLoyalty] = useState(null);

  // Fetch loyalty eligibility once on mount so the Pro card can show the
  // $12 first-month pitch when the server says the user qualifies. Server-
  // authoritative; the client never decides the discount itself.
  React.useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    paymentsApi.loyaltyEligibility()
      .then((res) => { if (!cancelled) setLoyalty(res); })
      .catch((e) => console.debug('[pricing] loyalty fetch:', e?.message));
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleUpgrade = async (packageId) => {
    setError(null);
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    // Auto-promote to the loyalty package when the user is eligible — the
    // server still re-validates so a client tamper just bounces with 403.
    const effectivePkg = (
      packageId === 'pro_monthly' && loyalty?.eligible
    ) ? 'pro_monthly_loyalty' : packageId;
    try {
      setCheckoutLoading(true);
      const { url } = await paymentsApi.createCheckout(effectivePkg);
      if (!url) throw new Error('Checkout URL missing');
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Could not start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setError(null);
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }
    try {
      setTrialLoading(true);
      await paymentsApi.startTrial();
      await refreshProfile();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Could not start trial');
    } finally {
      setTrialLoading(false);
    }
  };

  const tierButton = (t) => {
    if (t.id === 'free') {
      return (
        <Button
          data-testid="pricing-free-btn"
          disabled={tier === 'free' || !isAuthenticated}
          variant="outline"
          className="w-full border-gray-700 text-gray-300"
          onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
        >
          {tier === 'free' ? 'Current Plan' : 'Stay on Free'}
        </Button>
      );
    }
    if (t.id === 'teams') {
      return (
        <Button
          data-testid="pricing-teams-cta"
          className="w-full bg-[#9D00FF] text-white hover:bg-[#8800DD]"
          onClick={() => {
            if (!isAuthenticated) {
              setShowAuth(true);
              return;
            }
            navigate('/teams');
          }}
        >
          <Users className="w-4 h-4 mr-2" />
          {isAuthenticated ? 'Manage Team' : 'Create Team'}
        </Button>
      );
    }
    // Pro
    const isCurrent = tier === 'pro';
    const canStartTrial = isAuthenticated && !trialUsed && !isCurrent;
    return (
      <div className="space-y-2">
        <Button
          data-testid="pricing-pro-upgrade-btn"
          disabled={checkoutLoading || isCurrent}
          onClick={() => handleUpgrade(t.id)}
          className="w-full bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
        >
          {(() => {
            // Flattened from a 3-branch nested ternary so each state is
            // obvious and can grow independently.
            if (checkoutLoading) return <Loader2 className="w-4 h-4 animate-spin" />;
            if (isCurrent) {
              return (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Current Plan
                </>
              );
            }
            return (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </>
            );
          })()}
        </Button>
        {canStartTrial && (
          <Button
            data-testid="pricing-start-trial-btn"
            variant="outline"
            disabled={trialLoading}
            onClick={handleStartTrial}
            className="w-full border-[#00FF00]/40 text-[#00FF00] hover:bg-[#00FF00]/10"
          >
            {trialLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Gift className="w-4 h-4 mr-2" />
                Start 14-day free trial
              </>
            )}
          </Button>
        )}
        {isAuthenticated && trialUsed && !isCurrent && (
          <p className="text-xs text-gray-500 text-center">Trial already used on this account.</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="pricing-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 mt-16">
        <div className="text-center mb-14">
          <Badge className="bg-[#00FF00]/10 text-[#00FF00] border-[#00FF00]/40 mb-4">
            PRICING
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Train like an <span className="text-[#00FF00]">attacker</span>.
            <br />
            Defend like a <span className="text-[#00BFFF]">pro</span>.
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            Simple plans. Cancel anytime. Every tier unlocks more of the terminal, AI hints, and real-world
            scenarios.
          </p>
          {error && (
            <p className="text-red-400 mt-4" data-testid="pricing-error">
              {error}
            </p>
          )}
        </div>

        {/* Live Pro terminal preview */}
        <div className="max-w-4xl mx-auto mb-14">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-semibold text-white">
              The real Pro terminal
            </h2>
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              autoplay · not a recording
            </p>
          </div>
          <PricingTerminalDemo height={240} />
          <p className="text-xs text-gray-500 mt-3 text-center">
            This is the same bash PTY sandbox you get inside every Pro room.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => {
            const highlight = t.id === 'pro_monthly';
            const showLoyaltyPitch = highlight && loyalty?.eligible;
            return (
              <Card
                key={t.id}
                data-testid={`pricing-card-${t.id}`}
                className={`bg-[#1A1A1A] relative overflow-hidden ${
                  highlight
                    ? 'border-[#00FF00] shadow-[0_0_40px_rgba(0,255,0,0.15)]'
                    : 'border-gray-800'
                }`}
              >
                {t.badge && (
                  <div
                    className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold"
                    style={{
                      backgroundColor: `${t.color}20`,
                      color: t.color,
                      borderBottomLeftRadius: '0.5rem',
                    }}
                  >
                    {t.badge}
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5" style={{ color: t.color }} />
                    <CardTitle className="text-white text-xl">{t.name}</CardTitle>
                  </div>
                  <div className="flex items-baseline gap-1">
                    {showLoyaltyPitch ? (
                      <>
                        <span className="text-4xl font-bold text-[#00FF00]" data-testid="pricing-loyalty-amount">
                          ${loyalty.discountedAmount}
                        </span>
                        <span className="text-gray-400 text-sm">first month</span>
                        <span className="ml-2 text-sm text-gray-500 line-through">
                          ${loyalty.regularAmount}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">{t.price}</span>
                        <span className="text-gray-400 text-sm">{t.cadence}</span>
                      </>
                    )}
                  </div>
                  {showLoyaltyPitch && (
                    <p
                      className="text-xs text-[#00FF00] mt-1 font-semibold"
                      data-testid="pricing-loyalty-pitch"
                    >
                      Welcome back — one-time loyalty offer
                    </p>
                  )}
                  <p className="text-gray-400 text-sm mt-2">{t.tagline}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-200">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: t.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                    {t.missing.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-gray-600 line-through">
                        <span className="w-4 h-4 mt-0.5 flex-shrink-0">✕</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {tierButton(t)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {showAuth && (
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} initialTab="login" />
      )}
      <Footer />
    </div>
  );
};

export default Pricing;
